import { useId } from "react";
import { cn } from "@/lib/cn";
import { FieldFrame, fieldStateClasses, fieldSurface } from "./field";

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  containerClassName?: string;
}

/** Native select styled as a dark glass field with a custom chevron. */
export function Select({
  label,
  hint,
  error,
  required,
  id,
  className,
  containerClassName,
  children,
  "aria-describedby": ariaDescribedBy,
  ...props
}: SelectProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hasError = Boolean(error);
  const errorId = `${inputId}-error`;
  const describedBy =
    [ariaDescribedBy, hasError ? errorId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      errorId={errorId}
      required={required}
      htmlFor={inputId}
      className={containerClassName}
    >
      <div className="relative">
        <select
          id={inputId}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={cn(
            fieldSurface,
            fieldStateClasses(hasError),
            "h-11 cursor-pointer appearance-none pr-10",
            // native option list follows the OS; force dark text there
            "[&>option]:bg-elevated [&>option]:text-white",
            className,
          )}
          {...props}
        >
          {children}
        </select>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className="pointer-events-none absolute inset-y-0 right-3.5 my-auto size-4 text-white/45"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 8 4 4 4-4" />
        </svg>
      </div>
    </FieldFrame>
  );
}
